import * as React from "react"
import { cn } from "../../../lib/utils"

export interface FormattedDateProps extends React.HTMLAttributes<HTMLSpanElement> {
  date: Date | string | number
  type?: 'date' | 'time' | 'datetime' | 'relative'
  preset?: 'readable' | 'short' | 'compact'
  options?: Intl.DateTimeFormatOptions
}

export const FormattedDate = React.forwardRef<HTMLSpanElement, FormattedDateProps>(
  ({ date, type = 'date', preset = 'readable', options, className, ...props }, ref) => {
    const [formatted, setFormatted] = React.useState<string>("")

    React.useEffect(() => {
      const d = date instanceof Date ? date : new Date(date)
      
      if (isNaN(d.getTime())) {
        setFormatted(String(date))
        return
      }

      // Handle relative time separately if needed (can expand later)
      if (type === 'relative') {
        const now = new Date()
        const diff = now.getTime() - d.getTime()
        const minutes = Math.floor(diff / 60000)
        const hours = Math.floor(minutes / 60)
        const days = Math.floor(hours / 24)

        if (minutes < 1) setFormatted("Just now")
        else if (minutes < 60) setFormatted(`${minutes}m ago`)
        else if (hours < 24) setFormatted(`${hours}h ago`)
        else if (days < 7) setFormatted(`${days}d ago`)
        else {
          setFormatted(d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }))
        }
        return
      }

      let defaultOptions: Intl.DateTimeFormatOptions = {}

      if (preset === 'readable') {
        defaultOptions = {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }
        if (type === 'datetime') {
          defaultOptions = { ...defaultOptions, hour: 'numeric', minute: '2-digit' }
        }
      } else if (preset === 'short') {
        defaultOptions = {
          day: 'numeric',
          month: 'short',
          year: '2-digit'
        }
      } else if (preset === 'compact') {
        defaultOptions = {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }
      }

      if (type === 'time') {
        defaultOptions = { hour: 'numeric', minute: '2-digit' }
      }

      setFormatted(new Intl.DateTimeFormat(undefined, { ...defaultOptions, ...options }).format(d))
    }, [date, type, preset, options])

    return (
      <span ref={ref} className={cn("", className)} {...props}>
        {formatted}
      </span>
    )
  }
)

FormattedDate.displayName = "FormattedDate"
